import React from 'react'

// Components
import { Tooltip, useResponsiveDropdown } from '@sketch/components'

// Icons
import { ReactComponent as BellIcon } from '@sketch/icons/bell-16'
import { ReactComponent as BellStrikeIcon } from '@sketch/icons/bell-strikethrough-16'

// Internal Components
import { Action } from './DocumentNotificationButton.styles'

import NotificationStatusDropdown from 'modules/annotations/components/NotificationPartialStatusDropdown'
import useDocumentNotificationSubscription from 'modules/annotations/operations/useNotificationSubscription'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { NotificationSubscriptionStatus } from '@sketch/gql-types/expansive'

const labels: { [key in NotificationSubscriptionStatus]: string } = {
  ON: 'Following',
  OFF: 'Follow',
  PARTIAL: 'Following…',
} as const

const icons: { [key in NotificationSubscriptionStatus]: React.ElementType } = {
  ON: BellIcon,
  OFF: BellStrikeIcon,
  PARTIAL: BellIcon,
} as const

const buttonVariant: { [key in NotificationSubscriptionStatus]: string } = {
  ON: 'secondary',
  OFF: 'secondary-untinted',
  PARTIAL: 'secondary',
} as const

export const tooltipCopy: {
  [key in NotificationSubscriptionStatus]: string
} = {
  OFF: 'Receive updates on new comments',
  ON: 'Stop receiving updates on new comments',
  PARTIAL: 'You’re receiving updates on some comments in this document',
} as const

export interface DocumentToggleProps {
  identifier: string
  currentStatus: NotificationSubscriptionStatus
}

const DocumentNotificationButton: React.FC<DocumentToggleProps> = props => {
  const { currentStatus, identifier } = props
  const updateNotificationSubscriptions = useDocumentNotificationSubscription()
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: NotificationStatusDropdown,
    dropdownProps: {
      type: 'share',
      notificationStatus: currentStatus,
      onNotificationChange: state =>
        updateNotificationSubscriptions({
          shareIdentifier: identifier,
          state,
        }),
    },
  })

  const getButton = () => {
    if (currentStatus === 'PARTIAL') {
      return (
        <>
          <Action
            variant={buttonVariant[currentStatus]}
            icon={icons[currentStatus]}
            data-testid="document-notification-status"
            size="32"
            {...buttonProps}
          >
            {labels[currentStatus]}
          </Action>
          {content}
        </>
      )
    } else {
      const newStatus = currentStatus === 'ON' ? 'OFF' : 'ON'
      return (
        <>
          <Action
            variant={buttonVariant[currentStatus]}
            icon={icons[currentStatus]}
            data-testid="document-notification-status"
            size="32"
            onClick={() => {
              updateNotificationSubscriptions({
                shareIdentifier: identifier,
                state: newStatus,
              })
            }}
          >
            {labels[currentStatus]}
          </Action>
        </>
      )
    }
  }

  return (
    <Tooltip placement="top-start" content={tooltipCopy[currentStatus]}>
      {getButton()}
    </Tooltip>
  )
}

export default DocumentNotificationButton
